import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../config/firebase';
import { firebaseAuthService, clearAllSessionAndTenantStorage } from '../services/firebaseAuthService';
import { dbService } from '../services/firebase';
import { FirebaseUserData, UserProfile, UserRole } from '../types';

export interface AuthContextType {
  currentUser: FirebaseUserData | null;
  firebaseUser: FirebaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isEmailVerified: boolean;
  users: UserProfile[];
  loginWithEmail: (email: string, password: string) => Promise<FirebaseUserData>;
  registerWithEmail: (
    email: string,
    password: string,
    displayName: string,
    companyName?: string,
    sector?: string
  ) => Promise<FirebaseUserData>;
  loginWithGoogle: () => Promise<FirebaseUserData>;
  sendPasswordReset: (email: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  logout: () => Promise<void>;
  loginAs: (userId: string) => Promise<void>;
  updateProfile: (profile: Partial<FirebaseUserData>) => Promise<void>;
  refreshUserData: () => Promise<void>;
  demoLogin: (type: 'corporate' | 'yacht' | 'admin') => Promise<FirebaseUserData>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [currentUser, setCurrentUser] = useState<FirebaseUserData | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasVerifiedSuperAdmin, setHasVerifiedSuperAdmin] = useState<boolean>(false);

  // Sync users list from DB
  const syncUsersList = async () => {
    try {
      const allUsers = await dbService.getUsers();
      setUsers(allUsers);
    } catch (e) {
      console.warn('Sync users list notice:', e);
    }
  };

  // Listen to Firebase Auth state changes
  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;
      setIsLoading(true);
      setFirebaseUser(user);

      if (user) {
        let isSuperAdminServer = false;
        const userEmail = (user.email || '').toLowerCase().trim();

        // STRICT SECURITY RULE: Only admin@adiosoft.com with UID zrL9FtQF42aSt7cNDDVrzBAYOEP2 can be Super Admin
        if (userEmail === 'admin@adiosoft.com') {
          try {
            // Force refresh ID token to get authoritative custom claims from Firebase
            const tokenResult = await user.getIdTokenResult(true);
            const claimRole = (tokenResult.claims.role || tokenResult.claims.userRole || '') as string;
            const hasClaim = Boolean(
              tokenResult.claims.super_admin === true ||
              tokenResult.claims.isSuperAdmin === true ||
              claimRole === 'super_admin'
            );

            // Authoritative server-side verification check
            const token = await user.getIdToken();
            const verifyRes = await fetch('/api/auth/verify-role', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            });
            if (verifyRes.ok) {
              const verifyData = await verifyRes.json();
              if (verifyData.isSuperAdmin) {
                isSuperAdminServer = true;
              }
            } else if (hasClaim || user.uid === 'zrL9FtQF42aSt7cNDDVrzBAYOEP2') {
              isSuperAdminServer = true;
            }
          } catch (claimErr) {
            console.warn('Super admin claim verification notice:', claimErr);
            if (user.uid === 'zrL9FtQF42aSt7cNDDVrzBAYOEP2') {
              isSuperAdminServer = true;
            }
          }
        }

        if (isMounted) {
          setHasVerifiedSuperAdmin(isSuperAdminServer);
        }

        // Fetch user data from Realtime Database / /users/{uid}
        const profile = await firebaseAuthService.getUserProfile(user.uid);
        if (profile && isMounted) {
          if (userEmail === 'admin@adiosoft.com' && (isSuperAdminServer || user.uid === 'zrL9FtQF42aSt7cNDDVrzBAYOEP2')) {
            profile.role = 'super_admin';
            setHasVerifiedSuperAdmin(true);
          } else {
            // Force customer role for any non-admin email
            profile.role = 'customer';
            setHasVerifiedSuperAdmin(false);
          }
          setCurrentUser(profile);
        } else if (isMounted) {
          const isSuper = userEmail === 'admin@adiosoft.com' && (isSuperAdminServer || user.uid === 'zrL9FtQF42aSt7cNDDVrzBAYOEP2');
          // If no profile exists yet in /users/{uid}, create basic record
          const newProfile: FirebaseUserData = {
            uid: user.uid,
            email: user.email || '',
            displayName: user.displayName || (isSuper ? 'Cihan Alp Aygün' : (user.email?.split('@')[0] || 'Müşteri')),
            photoURL: user.photoURL || null,
            role: isSuper ? 'super_admin' : 'customer',
            status: 'active',
            createdAt: Date.now(),
            updatedAt: Date.now(),
            lastLoginAt: Date.now(),
            emailVerified: user.emailVerified,
          };
          await firebaseAuthService.saveUserProfile(newProfile);
          if (isSuper) setHasVerifiedSuperAdmin(true);
          setCurrentUser(newProfile);
        }
      } else {
        if (isMounted) {
          setHasVerifiedSuperAdmin(false);
          setCurrentUser(null);
        }
      }

      await syncUsersList();
      if (isMounted) {
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const loginWithEmail = async (email: string, password: string): Promise<FirebaseUserData> => {
    setIsLoading(true);
    try {
      const { user, userData } = await firebaseAuthService.loginWithEmail(email, password);
      setFirebaseUser(user);
      setCurrentUser(userData);
      await syncUsersList();
      return userData;
    } finally {
      setIsLoading(false);
    }
  };

  const registerWithEmail = async (
    email: string,
    password: string,
    displayName: string,
    companyName?: string,
    sector?: string
  ): Promise<FirebaseUserData> => {
    setIsLoading(true);
    try {
      const { user, userData } = await firebaseAuthService.registerWithEmail(
        email,
        password,
        displayName,
        companyName,
        sector
      );
      setFirebaseUser(user);
      setCurrentUser(userData);
      await syncUsersList();
      return userData;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (): Promise<FirebaseUserData> => {
    setIsLoading(true);
    try {
      const { user, userData } = await firebaseAuthService.loginWithGoogle();
      setFirebaseUser(user);
      setCurrentUser(userData);
      await syncUsersList();
      return userData;
    } finally {
      setIsLoading(false);
    }
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    await firebaseAuthService.sendPasswordReset(email);
  };

  const sendVerificationEmail = async (): Promise<void> => {
    await firebaseAuthService.sendEmailVerification(firebaseUser);
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await firebaseAuthService.logout();
      setFirebaseUser(null);
      setCurrentUser(null);
      setHasVerifiedSuperAdmin(false);
      clearAllSessionAndTenantStorage();
    } finally {
      setIsLoading(false);
    }
  };

  const loginAs = async (userId: string) => {
    setIsLoading(true);
    try {
      const profile = await firebaseAuthService.getUserProfile(userId);
      if (profile) {
        setCurrentUser(profile);
      } else {
        const allUsers = await dbService.getUsers();
        const found = allUsers.find((u) => u.uid === userId);
        if (found) {
          const userDat: FirebaseUserData = {
            uid: found.uid,
            email: found.email,
            displayName: found.displayName,
            photoURL: found.avatarUrl || null,
            role: found.role === 'SUPER_ADMIN' ? 'super_admin' : 'customer',
            status: 'active',
            createdAt: found.createdAt,
            updatedAt: Date.now(),
            lastLoginAt: Date.now(),
            companyName: found.companyName,
            emailVerified: true,
          };
          await firebaseAuthService.saveUserProfile(userDat);
          setCurrentUser(userDat);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const demoLogin = async (type: 'corporate' | 'yacht' | 'admin'): Promise<FirebaseUserData> => {
    setIsLoading(true);
    try {
      const data = await firebaseAuthService.demoLogin(type);
      setCurrentUser(data);
      await syncUsersList();
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (profileUpdates: Partial<FirebaseUserData>) => {
    if (!currentUser) return;
    const updated = await firebaseAuthService.updateUserProfile(currentUser.uid, profileUpdates);
    if (updated) {
      setCurrentUser(updated);
    }
    await syncUsersList();
  };

  const refreshUserData = async () => {
    if (currentUser?.uid) {
      const fresh = await firebaseAuthService.getUserProfile(currentUser.uid);
      if (fresh) setCurrentUser(fresh);
    }
    await syncUsersList();
  };

  const isAuthenticated = Boolean(currentUser);
  // STRICT ARCHITECTURE: Only admin@adiosoft.com (UID: zrL9FtQF42aSt7cNDDVrzBAYOEP2) with super_admin claim can be Super Admin
  const isSuperAdmin = Boolean(
    currentUser?.email?.toLowerCase() === 'admin@adiosoft.com' &&
    (currentUser?.uid === 'zrL9FtQF42aSt7cNDDVrzBAYOEP2' || hasVerifiedSuperAdmin || currentUser?.role === 'super_admin' || currentUser?.role === 'SUPER_ADMIN')
  );
  const isAdmin = isSuperAdmin;
  const isEmailVerified = Boolean(currentUser?.emailVerified || firebaseUser?.emailVerified);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        firebaseUser,
        isAuthenticated,
        isLoading,
        isAdmin,
        isSuperAdmin,
        isEmailVerified,
        users,
        loginWithEmail,
        registerWithEmail,
        loginWithGoogle,
        sendPasswordReset,
        sendVerificationEmail,
        logout,
        loginAs,
        updateProfile,
        refreshUserData,
        demoLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
